import {Injectable, OnDestroy} from '@angular/core';
import {AppStateService} from './app-state.service';
import {BehaviorSubject, firstValueFrom, mergeMap, Observable, of, Subscription, throwError, timer, EMPTY, merge, Subject, combineLatest} from 'rxjs';
import {map, switchMap, startWith} from 'rxjs/operators';
import {FireService} from './fire.service';
import {FSVoucher} from '../models/FSVoucher';

@Injectable({
  providedIn: 'root'
})
export class VoucherService implements OnDestroy {

  private sub1?: Subscription;
  private sub2?: Subscription;
  private vouchersSubject = new BehaviorSubject<FSVoucher[] | null>(null);
  private allVouchersSubject = new BehaviorSubject<FSVoucher[] | null>(null);
  private chatId: number | undefined;
  private timerSubject = new Subject<void>();
  private currentTimer?: Subscription;

  constructor(private fireService: FireService, private appStateService: AppStateService) {}

  private initService() {
    this.sub1 = this.appStateService.observeAppState().subscribe(appState => {
      console.log("VoucherService: AppState changed, chatId:", appState.order?.user_cfg?.chat_id);
      if (this.chatId !== appState.order?.user_cfg?.chat_id) {
        this.chatId = appState.order?.user_cfg?.chat_id;
        this.initVouchersObserver();
      }
    });
  }

  private initVouchersObserver() {
    // Clean up previous subscription and timer
    if (this.sub2) {
      this.sub2.unsubscribe();
      this.cancelTimer();
    }
    
    this.sub2 = combineLatest([
      this.fireService.observeVouchersForChat(this.chatId!),
      this.timerSubject.pipe(startWith(undefined)) // Initial emission to trigger immediately
    ]).subscribe(([vouchers]) => {
      // Store all vouchers without filtering
      this.allVouchersSubject.next(vouchers || []);
      // Store filtered vouchers for backward compatibility
      const activeVouchers = this.filterVouchersAndScheduleTimer(vouchers || []);
      this.vouchersSubject.next(activeVouchers);
    });
  }

  private filterVouchersAndScheduleTimer(vouchers: FSVoucher[]): FSVoucher[] {
    // Cancel any existing timer before setting up new one
    this.cancelTimer();
    
    const now = Date.now();
    const oneMinute = 60 * 1000;
    let nextExpirationTime: number | null = null;
    
    // Filter vouchers and find the next expiration time
    const filteredVouchers = vouchers.filter(voucher => {
      if (voucher.active) return true;

      // Check if applied date is within the last minute
      if (voucher.applied) {
        const appliedTime = voucher.applied.toDate().getTime();
        const expirationTime = appliedTime + oneMinute;
        
        if (expirationTime > now) {
          // Track the nearest expiration time
          if (nextExpirationTime === null || expirationTime < nextExpirationTime) {
            nextExpirationTime = expirationTime;
          }
          return true;
        }
      }

      return false;
    });

    // Schedule timer for the next expiration if needed
    if (nextExpirationTime !== null) {
      this.scheduleTimer(nextExpirationTime);
    }

    return filteredVouchers;
  }

  private scheduleTimer(expirationTime: number) {
    const now = Date.now();
    const delay = Math.max(0, expirationTime - now + 10); // Add 10ms buffer
    
    console.log(`VoucherService: Scheduling timer for ${delay}ms`);
    
    this.currentTimer = timer(delay).subscribe(() => {
      console.log('VoucherService: Timer fired, re-evaluating vouchers');
      this.timerSubject.next(); // Trigger re-evaluation
    });
  }

  private cancelTimer() {
    if (this.currentTimer) {
      console.log('VoucherService: Cancelling existing timer');
      this.currentTimer.unsubscribe();
      this.currentTimer = undefined;
    }
  }

  observeFilteredVouchers(): Observable<FSVoucher[] | null> {
    if (!this.sub1) {
      this.initService();
    }
    return this.vouchersSubject.asObservable();
  }

  observeActiveVouchers(): Observable<FSVoucher[] | null> {
    if (!this.sub1) {
      this.initService();
    }
    return this.allVouchersSubject.asObservable().pipe(
      map(vouchers => vouchers ? vouchers.filter(v => v.active) : null)
    );
  }

  observeInactiveVouchers(): Observable<FSVoucher[] | null> {
    if (!this.sub1) {
      this.initService();
    }
    return this.allVouchersSubject.asObservable().pipe(
      map(vouchers => vouchers ? vouchers.filter(v => !v.active) : null)
    );
  }

  ngOnDestroy() {
    // Clean up all subscriptions and timers
    this.sub1?.unsubscribe();
    this.sub2?.unsubscribe();
    this.cancelTimer();
    this.timerSubject.complete();
    this.vouchersSubject.complete();
    this.allVouchersSubject.complete();
  }
}
